import { Locales, localeToCountryUrlPrefix } from '../language'

import { UserServiceOfferDetails } from '@/features/service-offer/service-offer.type'
import { ServiceOffer } from '@/features/service-offer/service-offer.type'

import type { HospitalView } from '@/features/hospital/types/hospital.type'
import type { DoctorForUser } from '@/services/doctors/doctor.types'
import type {
  Organization,
  WithContext,
  Product,
  AggregateRating,
  Offer,
  BlogPosting,
  Physician,
  Review,
  MedicalBusiness,
} from 'schema-dts'

export * from './review.structured-data'

type MedicalBusinessSchema = WithContext<MedicalBusiness>

export const getMedicalClinicScheme = (
  locale: Locales,
  hospital: HospitalView,
  extra?: {
    review?: Review[] | Review
    doctors?: DoctorForUser[]
    events?: ServiceOffer[]
  },
): MedicalBusinessSchema | null => {
  try {
    const prefix = localeToCountryUrlPrefix(locale)
    return {
      '@context': 'https://schema.org',
      '@type': 'MedicalBusiness',
      name: hospital.name,
      image: hospital.images.mainImageUrl || hospital.images.profileImageUrl || '',
      logo: hospital.images.profileImageUrl || '',
      '@id': `https://www.gangnamunni.com/${prefix}hospitals/${hospital.hospitalId}`,
      url: `https://www.gangnamunni.com/${prefix}hospitals/${hospital.hospitalId}`,
      ...(hospital.statistic.rating && hospital.statistic.reviewCount
        ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: hospital.statistic.rating,
              reviewCount: hospital.statistic.reviewCount,
              bestRating: 10,
            },
          }
        : {}),
      address: {
        '@type': 'PostalAddress',
        streetAddress: hospital.location.address,
        addressLocality: hospital.location.sido || '',
        addressCountry: hospital.country,
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: hospital.location.lat,
        longitude: hospital.location.lng,
      },
      ...(extra?.review ? { review: extra.review } : {}),
      employee:
        extra?.doctors?.map((doctor) => ({
          '@type': 'EmployeeRole',
          employee: {
            '@type': 'Person',
            name: doctor.name,
          },
          roleName: 'Plastic Surgeon',
          url: `https://www.gangnamunni.com/${prefix}doctors/${doctor.doctorId}`,
        })) || [],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Cleaning services',
        itemListElement: [
          {
            '@type': 'OfferCatalog',
            name: 'Plastic Surgery',
            itemListElement:
              extra?.events?.map((event) => ({
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: event.title,
                  description: `${event.hospital?.name} | ${event.title}`,
                },
                price: event.price.discountedAmount,
                priceCurrency: event.price.currency,
              })) || [],
          },
        ],
      },
    }
  } catch (err) {
    console.error(err)
    return null
  }
}

export const getHospitalDoctorJsonLd = (doctor: DoctorForUser): WithContext<Physician> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Physician',
    name: doctor.name,
    medicalSpecialty: 'PlasticSurgery',
  }
}

/**
 * Organization
 * Google: https://developers.google.com/search/docs/appearance/structured-data/organization
 * Naver: https://searchadvisor.naver.com/guide/structured-data-channel
 */
interface OrganizationJsonLdPayload {
  name?: string
  description?: string
  title?: string
  logo?: string
  image?: string
  email?: string
  locale?: Locales
}
export const getOrganizationJsonLdWithContext = ({
  name,
  description,
  logo,
  image,
  email,
  locale,
}: OrganizationJsonLdPayload): WithContext<Organization> | null => {
  try {
    const prefix = locale ? localeToCountryUrlPrefix(locale) : ''
    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      url: `https://www.gangnamunni.com${prefix}`,
      logo: logo ? `https://www.gangnamunni.com${logo}` : 'https://www.gangnamunni.comimages/kr/logo-kr.svg',
      sameAs:
        locale === Locales.JA_JP
          ? [
              'https://www.instagram.com/gangnamunni_jp/',
              'https://www.youtube.com/channel/UCm7h6S-9amJETdvSBgzCvSw',
              'https://www.youtube.com/channel/UCm7h6S-9amJETdvSBgzCvSw',
              'https://twitter.com/gnun_jp',
              'https://line.me/R/ti/p/@434wqitx?oat__id=5001699',
              'https://play.google.com/store/apps/details?id=com.gangnam.sister',
              'https://itunes.apple.com/app/apple-store/id1035105027',
            ]
          : [
              'https://www.instagram.com/gangnamunni_kr/',
              'https://www.youtube.com/channel/UCm7h6S-9amJETdvSBgzCvSw',
              'https://www.facebook.com/gangnamunni.kr',
              'https://twitter.com/gnun_jp',
              'https://line.me/R/ti/p/@434wqitx?oat__id=5001699',
              'https://play.google.com/store/apps/details?id=com.gangnam.sister',
              'https://itunes.apple.com/app/apple-store/id1035105027',
            ],
      ...(image ? { image } : {}),
      ...(name ? { name } : {}),
      ...(description ? { description } : {}),
      ...(email ? { email } : {}),
    }
  } catch (err) {
    console.error(err)
    return null
  }
}

/**
 * Product
 * Google: https://developers.google.com/search/docs/data-types/product
 * Naver: https://searchadvisor.naver.com/guide/structured-data-rating
 */

interface HospitalProductJsonLdPayload {
  name: string
  description: string
  image: string
  priceCurrency: string
  price: number
  ratingValue?: number
  reviewCount?: number
}

export const getHospitalProductJsonLd = ({
  name,
  description,
  image,
  priceCurrency,
  price,
  ratingValue,
  reviewCount,
}: HospitalProductJsonLdPayload): Product | null => {
  const bestRating = 10
  try {
    const aggregateRating: AggregateRating | undefined =
      ratingValue && reviewCount
        ? {
            '@type': 'AggregateRating',
            ratingValue,
            reviewCount,
            bestRating,
          }
        : undefined

    const offers: Offer | undefined =
      priceCurrency && price
        ? {
            '@type': 'Offer',
            priceCurrency,
            price,
          }
        : undefined

    return {
      '@type': 'Product',
      name,
      description,
      image,
      ...(offers ? { offers } : {}),
      ...(aggregateRating ? { aggregateRating } : {}),
    }
  } catch (err) {
    console.error(err)
    return null
  }
}

export const getHospitalProductJsonLdWithContext = ({
  name,
  description,
  image,
  priceCurrency,
  price,
  ratingValue,
  reviewCount,
}: HospitalProductJsonLdPayload): WithContext<Product> | null => {
  const productJsonLd = getHospitalProductJsonLd({
    name,
    description,
    image,
    priceCurrency,
    price,
    ratingValue,
    reviewCount,
  })
  if (!productJsonLd) {
    return null
  }

  return {
    '@context': 'https://schema.org',
    ...productJsonLd,
  }
}

/**
 * BlogPosting
 * Google: https://developers.google.com/search/docs/data-types/article
 * Naver: N/A
 */
interface BlogPostingJsonLdPayload {
  postId: string
  headline: string
  image: string
  authorName: string
  datePublished: string
}

export const getBlogPostingJsonLd = ({
  postId,
  headline,
  image,
  authorName,
  datePublished,
}: BlogPostingJsonLdPayload): WithContext<BlogPosting> | null => {
  try {
    const jsonLdData: WithContext<BlogPosting> = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://www.gangnamunni.com/jp/posts/${postId}`,
      },
      headline,
      image,
      author: {
        '@type': 'Person',
        name: authorName,
      },
      publisher: {
        '@type': 'Organization',
        name: 'カンナムオンニ',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.gangnamunni.com/images/jp/logo.svg',
        },
      },
      datePublished,
    }
    return jsonLdData
  } catch (err) {
    console.error(err)
    return null
  }
}

export const getServiceOfferJsonLd = ({
  serviceOffer,
  optionId,
  reviews,
}: {
  serviceOffer: UserServiceOfferDetails
  // reviewResponse: ReviewsPaginationResponseInternal
  optionId?: string
  reviews?: Review | Review[]
}): WithContext<Product> | null => {
  try {
    const option = serviceOffer.options.find((option) => option.id === optionId) || serviceOffer.options[0]
    const price = option?.price.exchangeRateAppliedPrice?.discountedAmount || option?.price.discountedAmount || 0
    const priceCurrency = option?.price.exchangeRateAppliedPrice?.currency || option?.price.currency || ''

    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: serviceOffer.title,
      description: serviceOffer.description || '',
      image: [
        serviceOffer.thumbnail?.url,
        serviceOffer.cardImage?.url,
        ...serviceOffer.descriptionImages.map((img) => img.url),
      ].filter(Boolean),
      offers: {
        '@type': 'Offer',
        priceCurrency: serviceOffer.price.currency,
        price: serviceOffer.price.discountedAmount,
        availability: serviceOffer.available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        seller: {
          '@type': 'MedicalBusiness',
          name: serviceOffer.hospital.name || '',
          address: serviceOffer.hospital.location?.addresses[0].address || '',
        },
        priceSpecification: {
          '@type': 'PriceSpecification',
          valueAddedTaxIncluded: serviceOffer.pricePolicy.includeVat,
          price: serviceOffer.price.discountedAmount,
          priceCurrency: serviceOffer.price.currency,
        },
      },
      ...(priceCurrency && price
        ? {
            offers: {
              '@type': 'Offer',
              priceCurrency,
              price,
            },
          }
        : {}),
      ...(serviceOffer.rating.totalRating && serviceOffer.rating.ratingCount
        ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: serviceOffer.rating.totalRating,
              reviewCount: serviceOffer.rating.ratingCount,
              bestRating: 10,
            },
          }
        : {}),
      ...(reviews
        ? {
            review: reviews,
          }
        : {}),
    }
  } catch (err) {
    console.error(err)
    return null
  }
}
